import React, { useCallback, useState, useEffect, useRef } from "react";

import { CircularProgress, Box, TextField, Select, MenuItem, InputLabel, SelectChangeEvent } from "@mui/material";
import MarkerIcon from "@mui/icons-material/Room";
import { Autocomplete } from "@mui/material";
import MapGL, { Marker, type ViewStateChangeEvent } from "react-map-gl";
import { useRecoilValue } from "recoil";

import useDebounce from "hooks/useDebounce";
import { getMapboxCoordinates, getReverseGeocoding } from "services/GeoServices";
import { geolocationState } from "state/atoms";
import { generateYearArray } from "utils/date";
import { MapBoxListOption } from "utils/factories";

import { InputBoxes, DateBoxFormControl, StyledButton, ButtonLoading } from "./NewTreePageForm.styles";

export type Viewport = {
  latitude: number;
  longitude: number;
  zoom: number;
};

export type TreePageFormValues = {
  geoValues?: MapBoxListOption;
  yearAdded?: string;
};

export type TreePageSubmit = (args: TreePageFormValues) => void;

type NewTreePageFormProps = {
  loading: boolean;
  onSubmit: TreePageSubmit;
};

type ViewPort = {
  zoom: number;
  latitude: number;
  longitude: number;
};

const NewTreePageForm = ({ loading, onSubmit }: NewTreePageFormProps) => {
  const [debounceValue, inputValue, setInputValue] = useDebounce("", 1000);

  const [mapViewport, setMapViewport] = useState<ViewPort>({
    latitude: 0,
    longitude: 0,
    zoom: 16,
  });
  const [searchValue, setSearchValue] = useState<MapBoxListOption>();
  const [treeYear, setTreeDate] = useState("");
  const [options, setOptions] = useState<MapBoxListOption[]>([]);
  const mapMoveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const geo = useRecoilValue(geolocationState);

  const yearOptions = generateYearArray();

  const getCoordinates = useCallback(
    async (address: string) => {
      const features = await getMapboxCoordinates({ address, ...geo });
      setOptions(features);
    },
    [geo]
  );

  const getAddressFromCoordinates = useCallback(async (latitude: number, longitude: number) => {
    try {
      const features = await getReverseGeocoding({ latitude, longitude });
      if (features.length > 0) {
        setSearchValue(features[0]);
        setOptions(features);
      }
    } catch (error) {
      console.error("Error getting address from coordinates:", error);
    }
  }, []);

  const onInputChange = (_: any, value: string) => {
    setInputValue(value);
  };

  const handleSelectYear = (event: SelectChangeEvent<string>) => {
    setTreeDate(event.target.value);
  };

  useEffect(() => {
    setMapViewport((p) => ({ ...p, ...geo }));
  }, [geo]);

  useEffect(() => {
    if (debounceValue.length > 3) {
      getCoordinates(debounceValue);
    }
  }, [debounceValue, getCoordinates]);

  useEffect(() => {
    if (inputValue === "") {
      setOptions([]);
    }
  }, [inputValue]);

  const handleMove = useCallback(
    (e: ViewStateChangeEvent) => {
      setMapViewport((p) => ({ ...p, ...e.viewState }));
      if (mapMoveTimeoutRef.current) {
        clearTimeout(mapMoveTimeoutRef.current);
      }
      mapMoveTimeoutRef.current = setTimeout(() => {
        getAddressFromCoordinates(e.viewState.latitude, e.viewState.longitude);
      }, 1000);
    },
    [setMapViewport, getAddressFromCoordinates]
  );

  useEffect(() => {
    return () => {
      if (mapMoveTimeoutRef.current) {
        clearTimeout(mapMoveTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({
      geoValues: searchValue,
      yearAdded: treeYear,
    });
  }, [searchValue, treeYear, onSubmit]);

  return (
    <Box position="relative">
      <InputBoxes>
        <Autocomplete
          sx={{
            width: 300,
            "& .MuiInputBase-root": {
              backgroundColor: "#fff",
            },
          }}
          filterOptions={(x) => x}
          getOptionLabel={(x: MapBoxListOption | string) => (typeof x === "string" ? x : x.place)}
          options={options}
          autoComplete
          freeSolo
          includeInputInList
          filterSelectedOptions
          value={searchValue}
          onChange={(_, newValue) => {
            setSearchValue(newValue as MapBoxListOption);
            if (typeof newValue === "object" && newValue?.coordinates) {
              setMapViewport((p) => ({
                ...p,
                latitude: newValue.coordinates[1],
                longitude: newValue.coordinates[0],
              }));
            }
          }}
          onInputChange={onInputChange}
          renderInput={(params) => <TextField {...params} label="Add a location" variant="filled" fullWidth />}
        />
        <DateBoxFormControl variant="filled">
          <InputLabel id="select-tree-year">Tree Year</InputLabel>
          <Select
            sx={{
              backgroundColor: "#fff",
            }}
            labelId="select-tree-year"
            value={treeYear}
            onChange={handleSelectYear}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </DateBoxFormControl>
      </InputBoxes>
      <MapGL
        {...mapViewport}
        style={{ width: "100%", height: "60vh" }}
        mapStyle="mapbox://styles/mapbox/light-v9"
        onMove={handleMove}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
      >
        <Marker longitude={mapViewport.longitude} latitude={mapViewport.latitude}>
          <MarkerIcon />
        </Marker>
      </MapGL>
      <Box position="relative" justifyContent="center" alignItems="center" display="flex" mt={4}>
        <StyledButton variant="contained" color="primary" size="large" disabled={loading} onClick={handleSubmit}>
          Create
        </StyledButton>
        {loading && (
          <ButtonLoading>
            <CircularProgress size={28} />
          </ButtonLoading>
        )}
      </Box>
    </Box>
  );
};

export default NewTreePageForm;
